// Repository-based editorial content. No database, no AI generation.
// Curated educational copy is reviewed before publication and kept intentionally finite.

export const WHATS_MOVING = [
  {
    id: "rates",
    tag: "Macro",
    title: "Interest-rate expectations",
    body: "Shifting expectations for central-bank policy remain an important driver across asset classes. Lower expected rates can support gold and risk assets, while a stronger rate outlook can pressure them.",
  },
  {
    id: "dollar",
    tag: "Currencies",
    title: "US dollar strength",
    body: "Many globally traded metals and commodities are quoted in US dollars, so dollar moves can affect how expensive they are for buyers using other currencies.",
  },
  {
    id: "energy",
    tag: "Energy",
    title: "Supply & demand in oil",
    body: "Production decisions, inventories and demand signals influence Brent and WTI, while natural gas is also strongly affected by weather, storage and regional infrastructure.",
  },
  {
    id: "risk",
    tag: "Sentiment",
    title: "Risk appetite in crypto",
    body: "Bitcoin and Ethereum can be sensitive to broad risk sentiment and liquidity conditions. Flows in the largest assets may coincide with wider moves across crypto markets.",
  },
];

export const LEARN_ARTICLES = [
  {
    slug: "what-moves-gold-prices",
    title: "What Moves Gold Prices?",
    summary: "Real yields, the dollar, central-bank demand and safe-haven flows.",
    published: "2026-08-26",
    updated: "2026-09-08",
    related: [{ to: "/gold", label: "Gold price" }, { to: "/tools/gold-calculator", label: "Gold Value Calculator" }],
    body: [
      "Gold is a globally traded precious metal whose price reflects several forces at once. No single indicator explains every move, and relationships that are strong in one market regime can weaken in another.",
      "A useful way to read gold is to separate monetary drivers such as real yields and the US dollar from physical demand, official-sector buying and temporary demand for perceived safe havens.",
    ],
    sections: [
      {
        heading: "Real yields and the opportunity cost of holding gold",
        body: [
          "Gold does not pay interest. When inflation-adjusted yields on high-quality bonds rise, investors have a more attractive income-producing alternative, which can weigh on gold. When real yields fall, that opportunity cost is lower. This is an economic relationship rather than a mechanical rule: gold can still rise during periods of rising yields if other forces are stronger.",
        ],
      },
      {
        heading: "Why the US dollar matters",
        body: [
          "International gold benchmarks are commonly quoted in US dollars. A stronger dollar can make the same ounce more expensive for buyers using euros, pounds or other currencies, while a weaker dollar can have the opposite effect. Currency moves therefore help explain why gold performance can look different depending on the investor's home currency.",
        ],
      },
      {
        heading: "Central banks, investment flows and physical demand",
        body: [
          "Central-bank purchases can influence longer-term demand, while exchange-traded products, futures positioning, bars, coins and jewellery contribute in different ways. These channels do not move together all the time, so a change in one source of demand should not be treated as a complete explanation for price direction.",
        ],
      },
      {
        heading: "Safe-haven demand and how to read a move",
        body: [
          "Periods of financial stress or geopolitical uncertainty can increase demand for gold, but the response depends on liquidity, the dollar and interest rates at the same time. When a large move occurs, compare multiple timeframes and check whether it coincides with changes in yields, currency markets or broader risk sentiment before assigning a single cause.",
        ],
      },
    ],
  },
  {
    slug: "what-affects-bitcoin",
    title: "What Affects Bitcoin's Price?",
    summary: "Liquidity, risk sentiment, supply schedule and adoption.",
    published: "2026-08-26",
    updated: "2026-09-08",
    related: [{ to: "/bitcoin", label: "Bitcoin price" }, { to: "/tools/dca-calculator", label: "DCA Calculator" }],
    body: [
      "Bitcoin trades continuously across global venues, so its price can react quickly to macroeconomic news, changes in market liquidity and crypto-specific events.",
      "Its issuance schedule is transparent, but a known supply path does not make short-term demand predictable. Price still depends on what buyers and sellers are willing to transact at in the market.",
    ],
    sections: [
      {
        heading: "Liquidity and risk appetite",
        body: [
          "Bitcoin has often behaved like a high-volatility risk asset over shorter periods. Changes in interest-rate expectations, financial conditions and investor appetite for risk can affect demand. Correlation with equities or other assets is not constant, so recent co-movement should not be assumed to persist.",
        ],
      },
      {
        heading: "Supply, halvings and market structure",
        body: [
          "Bitcoin's protocol limits issuance and periodically reduces the block subsidy through halving events. That affects the rate of new supply, but market price also depends on existing holders, exchange liquidity, derivatives positioning and demand. A halving is therefore a supply event, not a guaranteed price signal.",
        ],
      },
      {
        heading: "Institutional flows, regulation and access",
        body: [
          "Changes in regulated investment access, custody, exchange availability and institutional flows can alter who can buy or sell Bitcoin and at what scale. Regulatory or legal developments can also change expectations quickly, particularly when they affect major markets or service providers.",
        ],
      },
      {
        heading: "24/7 trading, leverage and volatility",
        body: [
          "Because Bitcoin trades around the clock, sharp moves can happen outside traditional market hours. Leveraged positions can amplify short-term moves through liquidations. Use 24-hour, 7-day and 30-day changes together, and treat volatility as a description of past movement rather than a forecast of the next move.",
        ],
      },
    ],
  },
  {
    slug: "what-moves-oil-prices",
    title: "What Moves Oil Prices?",
    summary: "Supply decisions, demand cycles, inventories and geopolitics.",
    published: "2026-08-26",
    updated: "2026-09-08",
    related: [{ to: "/oil/brent", label: "Brent Crude" }, { to: "/compare/brent-vs-wti", label: "Brent vs WTI" }],
    body: [
      "Crude oil prices emerge from the balance between available supply and demand for physical barrels, but benchmark prices also reflect expectations about what that balance may look like in the future.",
      "Brent and WTI usually respond to many of the same global forces, yet local logistics, quality differences and regional inventories can cause their prices to diverge.",
    ],
    sections: [
      {
        heading: "Producer decisions and available supply",
        body: [
          "Production changes by major exporters can alter the expected supply balance. Unplanned outages, maintenance, sanctions and changes in non-OPEC production matter as well. The market often reacts to the difference between what was expected and what is actually delivered, not just to the headline production number.",
        ],
      },
      {
        heading: "Economic activity and oil demand",
        body: [
          "Transport, aviation, petrochemicals and industrial activity contribute to oil demand. Growth slowdowns can weaken demand expectations, while stronger activity can increase them. Seasonal travel and refinery maintenance also affect short-term patterns, so month-to-month comparisons need context.",
        ],
      },
      {
        heading: "Inventories, refining and benchmark differences",
        body: [
          "Inventory data helps show whether supply is accumulating or being drawn down, but the location and type of inventory matter. Refinery capacity, pipeline constraints and storage conditions can affect WTI differently from Brent, which is one reason the spread between the benchmarks changes over time.",
        ],
      },
      {
        heading: "Geopolitics and sudden risk premiums",
        body: [
          "Conflict or disruption near producing regions and shipping routes can add a risk premium when traders see a credible threat to supply. Not every geopolitical event removes barrels from the market, so an initial price spike may fade if physical flows remain intact. Compare headlines with evidence of actual supply disruption.",
        ],
      },
    ],
  },
  {
    slug: "understanding-market-volatility",
    title: "What Is Market Volatility?",
    summary: "Why prices swing, and what volatility does and doesn't tell you.",
    published: "2026-08-26",
    updated: "2026-09-08",
    related: [{ to: "/tools/percentage-change", label: "Percentage Change Calculator" }],
    body: [
      "Volatility describes the size and frequency of price movements. It does not tell you whether an asset is fundamentally cheap or expensive, and it does not indicate direction by itself.",
      "The same percentage move can mean very different things for a major currency pair, a commodity or a smaller cryptocurrency because each market has its own typical range of movement and liquidity.",
    ],
    sections: [
      {
        heading: "Realized versus expected volatility",
        body: [
          "Realized volatility summarizes movements that have already occurred. Options markets can also imply a market expectation for future volatility, but that expectation may be wrong. MARDATIQ's displayed volatility statistics should be read as descriptive market context rather than a promise about future risk.",
        ],
      },
      {
        heading: "Timeframe changes the interpretation",
        body: [
          "A 3% move in one day and a 3% move over six months are not equivalent observations. Short windows emphasize immediate shocks, while longer windows reveal whether those moves are unusual within a broader trend. Always compare volatility and returns using consistent periods.",
        ],
      },
      {
        heading: "Liquidity, leverage and event risk",
        body: [
          "Thin liquidity can make prices move more for the same amount of buying or selling. Leverage can amplify moves when positions are forced to close. Scheduled events such as central-bank decisions and unscheduled events such as geopolitical shocks can both create abrupt changes in volatility.",
        ],
      },
      {
        heading: "What volatility does not tell you",
        body: [
          "High volatility does not automatically mean an asset will fall, and low volatility does not guarantee safety. Volatility also omits some risks, including market closure, liquidity constraints and provider differences. Use it alongside price changes, timeframe and market structure rather than as a standalone signal.",
        ],
      },
    ],
  },
  {
    slug: "understanding-gold-karats",
    title: "Understanding Gold Karats: 24K, 22K, 21K, 18K and 14K",
    summary: "Karats, fineness and how purity affects value.",
    published: "2026-08-26",
    updated: "2026-09-08",
    related: [{ to: "/gold-price-per-gram", label: "Gold Price Per Gram" }, { to: "/tools/gold-calculator", label: "Gold Value Calculator" }],
    body: [
      "Karat is a measure of gold purity on a 24-part scale. A 24K item is close to pure gold, while lower-karat items contain a larger proportion of other metals.",
      "Purity matters when estimating raw metal value, but a jewellery purchase or resale price can differ substantially because real transactions include workmanship, margins, taxes, testing and dealer spreads.",
    ],
    sections: [
      {
        heading: "From karats to a purity percentage",
        body: [
          "The basic conversion is karat ÷ 24. That makes 22K about 91.67% gold, 21K 87.5%, 18K 75% and 14K about 58.33%. Fineness expresses a similar idea on a thousand-part scale, such as approximately 750 for 18K gold.",
        ],
      },
      {
        heading: "Estimating pure-gold content",
        body: [
          "Multiply the item's weight by its purity fraction. For example, 10 grams of 18K gold contains about 7.5 grams of pure gold by this theoretical calculation. The estimate assumes the stated karat is accurate and the measured weight represents the gold alloy being valued.",
        ],
      },
      {
        heading: "Turning pure grams into a metal-value estimate",
        body: [
          "Market gold is commonly quoted per troy ounce. Converting that reference price to a per-gram price and multiplying by pure-gold grams produces an approximate underlying metal value. Currency conversion is a separate step when the quote and display currency differ.",
        ],
      },
      {
        heading: "Why jewellery prices differ from spot value",
        body: [
          "Retail jewellery can include design, labour, brand and tax components, while a buyer of used jewellery may deduct refining, assay or business costs. Collectible or antique value can move in the opposite direction and exceed metal value. A gold calculator should therefore be treated as a reference for metal content, not a guaranteed transaction quote.",
        ],
      },
    ],
  },
  {
    slug: "gold-vs-bitcoin",
    title: "Gold vs Bitcoin: Understanding the Differences",
    summary: "How a physical precious metal and a digitally scarce asset differ in market structure, custody and volatility.",
    published: "2026-08-26",
    updated: "2026-09-08",
    related: [{ to: "/compare/gold-vs-bitcoin", label: "Gold vs Bitcoin comparison" }],
    body: [
      "Gold and Bitcoin are sometimes grouped together because both are discussed as scarce assets, but their histories, custody models and market behaviour are substantially different.",
      "A useful comparison focuses on characteristics rather than declaring a universal winner: supply, portability, market hours, volatility, custody and the economic forces that tend to influence demand.",
    ],
    sections: [
      {
        heading: "Scarcity works differently",
        body: [
          "Bitcoin's issuance rules are encoded in its protocol and its eventual maximum supply is known. Gold is naturally scarce, but new mine production and recycling add to the above-ground stock over time. Both are scarce in different senses, so their supply stories should not be treated as interchangeable.",
        ],
      },
      {
        heading: "Custody, transfer and market hours",
        body: [
          "Physical gold requires secure storage and can involve transport or assay costs. Bitcoin can be transferred digitally, but ownership depends on secure key management or a trusted custodian. Bitcoin trades continuously, while many gold trading venues follow market schedules even though global pricing spans multiple time zones.",
        ],
      },
      {
        heading: "Different drivers and volatility profiles",
        body: [
          "Gold is often sensitive to real yields, the US dollar and official-sector demand. Bitcoin can be more sensitive to crypto liquidity, leverage, regulation and risk appetite. Bitcoin has historically shown much larger percentage swings, so equal-sized allocations would not imply equal risk exposure.",
        ],
      },
      {
        heading: "How to compare performance responsibly",
        body: [
          "A normalized chart can show how two assets changed from the same starting point, but it removes information about volatility, drawdowns, fees and custody. The result describes a chosen historical period only. It should not be read as evidence that the asset with the stronger past return will outperform in the future.",
        ],
      },
    ],
  },
  {
    slug: "what-is-dollar-cost-averaging",
    title: "What Is Dollar-Cost Averaging?",
    summary: "Investing a fixed amount on a schedule to spread purchases across different prices.",
    published: "2026-08-26",
    updated: "2026-09-08",
    related: [{ to: "/tools/dca-calculator", label: "DCA Calculator" }],
    body: [
      "Dollar-cost averaging (DCA) is a schedule-based approach: the same amount is invested at regular intervals instead of selecting one entry price for the entire amount.",
      "It changes the timing of purchases, not the underlying risk of the asset. A disciplined schedule can reduce reliance on one entry point, but it cannot make a falling investment profitable by itself.",
    ],
    sections: [
      {
        heading: "How repeated purchases change the average entry price",
        body: [
          "When the contribution amount is fixed, lower prices buy more units and higher prices buy fewer. The eventual average cost per unit depends on every purchase. This can smooth the effect of timing compared with investing the full amount at one moment, although it can also lag a lump-sum investment when prices rise steadily from the start.",
        ],
      },
      {
        heading: "A simple worked example",
        body: [
          "Suppose three equal contributions of 100 are made at prices of 10, 20 and 25. They buy 10, 5 and 4 units respectively, for 19 units from 300 invested. The average cost per unit is about 15.79. The example shows the mechanics only; real results depend on actual prices and costs.",
        ],
      },
      {
        heading: "What DCA can and cannot solve",
        body: [
          "DCA can make a contribution plan easier to follow and reduce dependence on a single purchase date. It does not remove market risk, guarantee a gain or prevent losses during a sustained decline. Fees can also matter more when many small transactions are used.",
        ],
      },
      {
        heading: "How the MARDATIQ simulator approximates a schedule",
        body: [
          "The simulator uses evenly spaced points from the available historical series rather than reconstructing every exact execution timestamp. When a non-USD currency is selected, it uses the available current reference FX relationship rather than a complete historical FX series. This makes the result an educational estimate, not an account statement or backtest with execution-level precision.",
        ],
      },
    ],
  },
  {
    slug: "how-to-read-a-price-chart",
    title: "How to Read a Market Price Chart",
    summary: "Timeframes, trends and what a line chart can — and cannot — tell you.",
    published: "2026-08-26",
    updated: "2026-09-08",
    related: [{ to: "/markets", label: "Explore markets" }],
    body: [
      "A price chart compresses a sequence of observations into a visual timeline. Before interpreting its shape, identify the asset, quote currency, timeframe and whether the displayed data is live, delayed or a fallback.",
      "Charts are most useful for context: they show where the current observation sits relative to earlier ones. They do not explain causation and they cannot guarantee that a visual pattern will repeat.",
    ],
    sections: [
      {
        heading: "Start with the axes and timeframe",
        body: [
          "The horizontal axis represents time and the vertical axis represents price. Changing from 24 hours to one year can make the same recent move appear dramatic or minor. Compare like with like: a percentage return over one day is not directly comparable with a percentage return over one year without accounting for the different horizon.",
        ],
      },
      {
        heading: "Trend, range and percentage change",
        body: [
          "A rising line means later observations are above earlier ones; a falling line means the opposite. Recent highs and lows help define the visible range. Percentage change is useful because it scales the move relative to the starting value, making different price levels easier to compare.",
        ],
      },
      {
        heading: "Volatility and noisy short-term moves",
        body: [
          "A jagged chart indicates larger or more frequent changes, but visual appearance also depends on the scale and sampling frequency. A short-lived spike may dominate a one-day chart and barely register on a yearly chart. Use more than one timeframe before describing a move as a durable trend.",
        ],
      },
      {
        heading: "Avoid turning a chart into a prediction",
        body: [
          "Support, resistance and recurring shapes can be useful descriptive language, but historical geometry does not force future prices to behave the same way. News, liquidity and market structure can change abruptly. Treat the chart as evidence of what happened, then use additional context to investigate why.",
        ],
      },
    ],
  },
];

export const CATEGORY_CONTEXT = {
  metal: "what-moves-gold-prices",
  crypto: "what-affects-bitcoin",
  energy: "what-moves-oil-prices",
  forex: "understanding-market-volatility",
};

// Curated internal-linking map (genuinely helpful, not link spam).
export const RELATED_LINKS = {
  gold: [
    { to: "/tools/gold-calculator", key: "tools.goldCalc" },
    { to: "/gold-price-per-gram", key: "x.dcaCalc", label: "Gold Price Per Gram" },
    { to: "/learn/understanding-gold-karats", label: "Gold Karat Guide" },
    { to: "/compare/gold-vs-bitcoin", label: "Gold vs Bitcoin" },
  ],
  bitcoin: [
    { to: "/tools/crypto-profit-calculator", key: "tools.cryptoCalc" },
    { to: "/tools/dca-calculator", key: "x.dcaCalc" },
    { to: "/learn/what-affects-bitcoin", label: "Bitcoin Guide" },
    { to: "/compare/gold-vs-bitcoin", label: "Gold vs Bitcoin" },
  ],
  crypto: [
    { to: "/tools/crypto-profit-calculator", key: "tools.cryptoCalc" },
    { to: "/tools/dca-calculator", key: "x.dcaCalc" },
    { to: "/compare/bitcoin-vs-ethereum", label: "Bitcoin vs Ethereum" },
  ],
  oil: [
    { to: "/learn/what-moves-oil-prices", label: "Oil Guide" },
    { to: "/compare/brent-vs-wti", label: "Brent vs WTI" },
  ],
};

export const COMPARE_PAIRS = [
  { slug: "gold-vs-bitcoin", a: "gold", b: "bitcoin", title: "Gold vs Bitcoin" },
  { slug: "bitcoin-vs-ethereum", a: "bitcoin", b: "ethereum", title: "Bitcoin vs Ethereum" },
  { slug: "brent-vs-wti", a: "brent", b: "wti", title: "Brent vs WTI" },
  { slug: "gold-vs-silver", a: "gold", b: "silver", title: "Gold vs Silver" },
];
